/**
 * Word Collection AI Context Builder
 *
 * Builds compact AI context payloads from word collection state. Optimized for:
 * - Performance: <10ms build time
 * - Size: ≤1KB compressed
 * - Memoization: Stable references for unchanged data
 */

import type { Word } from '../../types/wordCollection'
import type { WordCollectionAIContext } from '../../types/wordCollectionAIContext'
import type { AIFeedbackPreferences } from '../../types/song'

/**
 * Safely convert a date value to timestamp (milliseconds).
 * Handles both Date objects and ISO string dates from JSON serialization.
 */
function toTimestamp(dateValue: Date | string | undefined | null): number {
  if (!dateValue) return 0
  if (dateValue instanceof Date) return dateValue.getTime()
  // ISO string from JSON - parse it
  const parsed = new Date(dateValue)
  return isNaN(parsed.getTime()) ? 0 : parsed.getTime()
}

/**
 * Safely convert a date value to Date object.
 * Handles both Date objects and ISO string dates from JSON serialization.
 */
function toDate(dateValue: Date | string | undefined | null): Date | null {
  if (!dateValue) return null
  if (dateValue instanceof Date) return dateValue
  const parsed = new Date(dateValue)
  return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Build AI context from word collection state.
 *
 * Performance requirements:
 * - Must complete in <10ms for collections up to 100 words
 * - Uses Map/Set for O(1) lookups
 * - Limits array sizes (5/10) to control payload
 *
 * Size requirements:
 * - Target ≤1KB compressed
 * - Limits to 5 top collected, 10 recent
 *
 * @param words - All words in collection
 * @param usedWordsInLyrics - Set of words used in lyrics
 * @param lyrics - Optional lyrics text for alignment feedback
 * @param stabilityLevel - Optional stability setting (0-100)
 * @param aiFeedbackPreferences - Optional AI feedback preferences from song settings
 * @returns Compact AI context payload
 */
export function buildAIContext(
  words: Word[],
  usedWordsInLyrics: Set<string>,
  lyrics?: string,
  stabilityLevel?: number,
  aiFeedbackPreferences?: AIFeedbackPreferences
): WordCollectionAIContext {
  // Count words by status - single pass through array
  // Maps two-state model to context fields:
  //   collected → collectedWords (primary working set)
  //   set_aside → setAsideWords (parked for later)
  let collectedCount = 0
  let setAsideCount = 0
  let usedCount = 0

  const collectedWordsList: Word[] = []
  const allWords: Word[] = []

  for (const word of words) {
    if (word.status === 'collected') {
      collectedCount++
      collectedWordsList.push(word)
    } else if (word.status === 'set_aside') {
      setAsideCount++
    }

    if (usedWordsInLyrics.has(word.word)) {
      usedCount++
    }

    allWords.push(word)
  }

  // Top 5 collected words by recency (last_active_at or discovered_at)
  const topCollected = collectedWordsList
    .sort((a, b) => {
      const aTime = toTimestamp(a.timeline.last_active_at) || toTimestamp(a.timeline.discovered_at)
      const bTime = toTimestamp(b.timeline.last_active_at) || toTimestamp(b.timeline.discovered_at)
      return bTime - aTime
    })
    .slice(0, 5)
    .map(w => w.word)

  // Most recent 10 words by discovered_at
  const recent = [...allWords]
    .sort((a, b) => toTimestamp(b.timeline.discovered_at) - toTimestamp(a.timeline.discovered_at))
    .slice(0, 10)
    .map(w => w.word)

  // Words that have been phonetically mapped vs. ungrouped
  // rhymes_explored flags whether the grouping engine has run on this word
  const rhymeGroupCount = words.filter(w => w.exploration.rhymes_explored).length
  const singletonCount = words.filter(w => !w.exploration.rhymes_explored).length

  // Exploration depth - how many words have explored rhymes/thesaurus (0-10 scale)
  const exploredCount = words.filter(w =>
    w.exploration.rhymes_explored || w.exploration.thesaurus_explored
  ).length
  const explorationDepth = words.length > 0
    ? Math.round((exploredCount / words.length) * 10)
    : 0

  // Last activity - most recent word modification
  let lastActivity: Date | null = null
  let lastActivityTs = 0
  for (const word of words) {
    const activityTs = toTimestamp(word.timeline.last_active_at) || toTimestamp(word.timeline.discovered_at)
    if (activityTs > lastActivityTs) {
      lastActivityTs = activityTs
      lastActivity = toDate(word.timeline.last_active_at) || toDate(word.timeline.discovered_at)
    }
  }

  return {
    totalWordCount: words.length,
    collectedWords: collectedCount,
    setAsideWords: setAsideCount,
    usedInLyrics: usedCount,
    topCollectedWords: topCollected,
    recentWords: recent,
    rhymeGroupCount,
    singletonCount,
    explorationDepth,
    lastActivity,
    lyrics,
    stabilityLevel,
    aiFeedbackPreferences
  }
}
